import React, { useRef, useEffect, useState, useCallback } from 'react';
import DragHandleMenu from './DragHandleMenu';
import ConnectionMenu from './ConnectionMenu';
import { DEFAULT_OPTIONS } from './DecisionOptionsModal';
import { CATEGORY_COLORS } from './constants';
import { useNodeResize, useConnectionMenu } from './hooks';
import useOnTaskIdentity from './hooks/useOnTaskIdentity';
import { useIconPicker } from '../../hooks/useIconPicker';
import ResizeHandles from './components/ResizeHandles';
import { MissionCardBody } from '../../shared/CardComponents';
import { getPrimaryTag } from '../../hooks/useTagActions';
import IconPicker from '../../shared/IconPicker';
import { isPointerOverPortaledOverlayFromEvent } from '../hooks/utils/overlayPointerGuard';
// Default dimensions - match TaskNode (mission cards)
const DEFAULT_WIDTH = 288;
const MIN_WIDTH = 200;
const MIN_HEIGHT = 140;

/**
 * DecisionNode Component
 * 
 * A thin wrapper around MissionCardBody for decision nodes.
 * Handles Flow-view specific concerns:
 * - Positioning via CSS transform
 * - Branch connection handles at bottom
 * - Resize handles
 * - Drag handle menu
 * 
 * The visual card content (including decision badge and answer toggle)
 * is rendered by MissionCardBody when task.nodeType === 'decision'.
 */
const DecisionNode = ({
  task,
  position,
  isSelected,
  isDragging,
  layoutSurfaceEpoch: _layoutSurfaceEpoch = 0,
  isConnecting,
  isConnectionTarget,
  isConnectionHovered,
  isFocusedMain,
  isFocusDependency,
  isAnimating,
  isFadingOut,
  isFadingIn,
  isDropTarget,
  connectingBranch,
  lockState,
  statuses,
  allCategories = [],
  allTags = [],
  allTasks = [],
  zoom = 1,
  onMouseDown,
  onClick,
  onConnectionDragStart,
  onStatusChange,
  onCategoryChange,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onIconChange,
  onIconDeleted,
  onRename,
  onResize,
  onResizeEnd,
  onResetSize,
  onDimensionsChange,
  onAnswerChange,
  onOptionsChange,
  onDelete,
  onCopy,
  onDuplicate,
  onFocusTask,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onRenameCategory,
  onDeleteCategory,
  onRenameTag,
  onDeleteTag,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onPriorityChange,
  onDifficultyChange,
  onCustomFieldValueChange,
  customFields = [],
  onArchiveTask,
  onUnarchiveTask,
  onConditionsChange,
  onAddMilestoneOnHost,
  onAddNoteOnHost,
  onConditionsDialogOpen,
  onConditionsDialogClose,
  onOpenDetails,
  onOpenAttachments,
  onGoToTask,
  cardDisplaySettings = {},
  isTemplate = false,
  // View navigation
  currentView,
  customViews = [],
  activeCustomViewId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  onCreateConnectedNode,
  checkClipboard,
  onCreateCampaign,
  isMultiCampaignMode = false,
  // Mission hover state (for bidirectional highlighting with HUD)
  isHoveredFromHUD = false,
  onNodeHoverForHUD,
  onNodeLeaveForHUD,
  // Deliverables warning support
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  onNavigateToParentMission,
}) => {
  const nodeRef = useRef(null);
  const [hoveredBranch, setHoveredBranch] = useState(null);

  useOnTaskIdentity(task.id, () => {
    setHoveredBranch(null);
  });
  const isLocked = lockState?.isLocked;
  const isExcluded = lockState?.isExcluded;

  const iconPicker = useIconPicker({
    taskId: task.id,
    onIconChange,
  });

  const { isResizing, handleResizeMouseDown } = useNodeResize({
    nodeRef,
    taskId: task.id,
    onResize,
    onResizeEnd,
    onResetSize,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    getZoom: () => zoom,
    getPosition: () => position,
  });

  const connectionMenu = useConnectionMenu({
    taskId: task.id,
    onCreateConnectedNode,
  });

  const branchMouseDownRef = useRef(null);
  const isBranchDraggingRef = useRef(false);

  const handleBranchMouseDown = useCallback((optionId) => (e) => {
    e.stopPropagation();
    e.preventDefault();

    branchMouseDownRef.current = {
      x: e.clientX,
      y: e.clientY,
      optionId,
    };
    isBranchDraggingRef.current = false;

    const handleMouseMove = (moveEvent) => {
      if (!branchMouseDownRef.current) return;
      const dx = Math.abs(moveEvent.clientX - branchMouseDownRef.current.x);
      const dy = Math.abs(moveEvent.clientY - branchMouseDownRef.current.y);
      if (dx > 5 || dy > 5) {
        isBranchDraggingRef.current = true;
        const rect = e.currentTarget.getBoundingClientRect();
        const screenPos = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
        onConnectionDragStart?.(e, optionId, screenPos);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        branchMouseDownRef.current = null;
      }
    };

    const handleMouseUp = (upEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (!isBranchDraggingRef.current && branchMouseDownRef.current) {
        const branchEl = upEvent.target.closest('.decision-node__branch-btn');
        if (branchEl) {
          const rect = branchEl.getBoundingClientRect();
          connectionMenu.openMenuFromPosition(
            {
              x: rect.left + rect.width / 2,
              y: rect.bottom,
            },
            branchMouseDownRef.current.optionId,
          );
        }
      }
      branchMouseDownRef.current = null;
      isBranchDraggingRef.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onConnectionDragStart, connectionMenu.openMenuFromPosition]);

  const nodeWidth = task.width || DEFAULT_WIDTH;
  const nodeHeight = task.height;

  // Get primary tag for styling (same as TaskNode)
  const primaryTagName = getPrimaryTag(task);
  const availableTags = allTags.length > 0 ? allTags : allCategories;
  const primaryTagObject = primaryTagName 
    ? availableTags.find(t => t.name === primaryTagName) || { name: primaryTagName, color: CATEGORY_COLORS[0] }
    : null;
  const currentCategory = primaryTagObject;

  // Get options for branch handles
  const options = task.options || DEFAULT_OPTIONS;
  const answer = task.answer || null;
  const selectedOption = answer ? options.find(opt => opt.id === answer) : null;

  // Report dimensions for layout
  // IMPORTANT: Debounce the ResizeObserver callback to prevent layout thrashing.
  // Without debouncing, transient layout states during re-renders (e.g., when
  // selection changes) can trigger dimension updates that cascade into
  // position adjustments via useVerticalSpacingOnResize.
  useEffect(() => {
    if (nodeRef.current && onDimensionsChange) {
      const node = nodeRef.current;
      let resizeTimer = null;
      
      const updateDimensions = () => {
        if (!nodeRef.current) return;
        const width = nodeRef.current.offsetWidth;
        const height = nodeRef.current.offsetHeight;
        onDimensionsChange(task.id, { width, height });
      };
      
      // Initial measurement (immediate)
      updateDimensions();
      
      // Debounced handler for subsequent resizes
      const debouncedUpdate = () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateDimensions, 50);
      };
      
      const observer = new ResizeObserver(debouncedUpdate);
      observer.observe(node);
      
      return () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        observer.disconnect();
      };
    }
  }, [task.id, onDimensionsChange]);

  // Handle node mouse down (start drag)
  const handleNodeMouseDown = (e) => {
    if (isPointerOverPortaledOverlayFromEvent(e)) {
      return;
    }
    if (e.target.closest('.decision-node__branch-btn') ||
        e.target.closest('.mission-card__name-input') ||
        e.target.closest('.mission-card__icon-btn') ||
        e.target.closest('.mission-card__decision-toggle') ||
        e.target.closest('.mission-card__decision-settings-btn') ||
        e.target.closest('.task-node__tags-btn') ||
        e.target.closest('.task-node__tags-menu') ||
        e.target.closest('.task-node__status-btn') ||
        e.target.closest('.task-node__status-menu') ||
        e.target.closest('.task-node__resize-handle') ||
        e.target.closest('.icon-picker') ||
        e.target.closest('.task-node__drag-menu') ||
        e.target.closest('.mission-card__toolbar-item') ||
        e.target.closest('.mission-card__attached-doc')) {
      return;
    }
    onMouseDown?.(e);
  };

  // Build class names
  const nodeClasses = [
    'decision-node',
    isSelected && 'decision-node--selected',
    isDragging && 'decision-node--dragging',
    isResizing && 'decision-node--resizing',
    isConnecting && 'decision-node--connecting',
    isConnectionTarget && 'decision-node--target',
    isConnectionHovered && 'decision-node--target-hovered',
    isFocusedMain && 'decision-node--focused-main',
    isFocusDependency && 'decision-node--focus-dependency',
    isAnimating && 'decision-node--animating',
    isFadingOut && 'decision-node--fading-out',
    isFadingIn && 'decision-node--fading-in',
    isDropTarget && 'decision-node--drop-target',
    isExcluded && 'decision-node--excluded',
    isLocked && 'decision-node--locked',
    currentCategory && 'decision-node--has-category',
    answer && 'decision-node--answered',
    isHoveredFromHUD && 'decision-node--hud-hover',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={nodeRef}
      className={nodeClasses}
      data-task-id={task.id}
      style={{
        '--node-tx': `${position.x}px`,
        '--node-ty': `${position.y}px`,
        width: nodeWidth,
        minHeight: task.height ? nodeHeight : MIN_HEIGHT,
        '--category-color': currentCategory?.color || 'transparent',
        '--category-bg': currentCategory ? `${currentCategory.color}18` : 'transparent',
        '--category-border': currentCategory ? `${currentCategory.color}35` : 'var(--border-color)',
        '--answer-color': selectedOption?.color || 'transparent',
      }}
      onMouseDown={handleNodeMouseDown}
      onClick={onClick}
      onMouseEnter={() => onNodeHoverForHUD?.()}
      onMouseLeave={() => onNodeLeaveForHUD?.()}
    >
      {/* Drag handle with menu */}
      <DragHandleMenu
        task={task}
        onDelete={onDelete}
        onCopy={onCopy}
        onDuplicate={onDuplicate}
        onFocusTask={onFocusTask}
        hasNestedSprint={false}
        onOpenNestedSprint={null}
        onCreateNestedSprint={null}
        hasIcon={!!task.icon}
        onAddIcon={iconPicker.openPicker}
        onAddMilestoneOnHost={onAddMilestoneOnHost}
        onAddNoteOnHost={onAddNoteOnHost}
      />

      {/* ═══════════════════════════════════════════════════════════════
          SHARED CARD BODY - All visual content including decision elements
          ═══════════════════════════════════════════════════════════════ */}
      <MissionCardBody
        task={task}
        category={currentCategory}
        allTags={availableTags}
        allTasks={allTasks}
        statuses={statuses}
        nestedSprint={null}
        isBlocked={false}
        isLocked={isLocked}
        lockReason={lockState?.lockReason}
        blockingDependencies={lockState?.blockingTasks || []}
        parentMissionName={lockState?.parentMissionName}
        parentCampaignName={lockState?.parentCampaignName}
        parentMissionId={lockState?.parentMissionId}
        parentSprintId={lockState?.parentSprintId}
        onNavigateToParentMission={onNavigateToParentMission}
        cardDisplaySettings={cardDisplaySettings}
        isTemplate={isTemplate}
        showCategoryBar={false} /* Wrapper handles category bar via ::before */
        onRename={onRename}
        onIconChange={onIconChange}
        onStatusChange={onStatusChange}
        onAddTag={onAddTag || onCategoryChange}
        onRemoveTag={onRemoveTag}
        onSetPrimaryTag={onSetPrimaryTag}
        onRenameTag={onRenameTag || onRenameCategory}
        onDeleteTag={onDeleteTag || onDeleteCategory}
        onOpenDetails={onOpenDetails}
        onOpenAttachments={onOpenAttachments}
        onOpenNestedSprint={null}
        onRemoveNestedSprint={null}
        onAddStatus={onAddStatus}
        onDeleteStatus={onDeleteStatus}
        onRenameStatus={onRenameStatus}
        onDueDateChange={onDueDateChange}
        onRemindersChange={onRemindersChange}
        onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
        onCreatedAtChange={onCreatedAtChange}
        onStartedAtChange={onStartedAtChange}
        onCompletedAtChange={onCompletedAtChange}
        onUpdatedAtChange={onUpdatedAtChange}
        onPriorityChange={onPriorityChange}
        onDifficultyChange={onDifficultyChange}
        onCustomFieldValueChange={onCustomFieldValueChange}
        customFields={customFields}
        onArchiveTask={onArchiveTask}
        onUnarchiveTask={onUnarchiveTask}
        onConditionsChange={onConditionsChange}
        onConditionsDialogOpen={onConditionsDialogOpen}
        onConditionsDialogClose={onConditionsDialogClose}
        onGoToDependency={onGoToTask}
        onSubmitDeliverable={onSubmitDeliverable}
        onEditDeliverables={onEditDeliverables}
        onClearDeliverable={onClearDeliverable}
        currentView={currentView}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
        onViewChange={onViewChange}
        onSelectCustomView={onSelectCustomView}
        onGoToTaskInView={onGoToTaskInView}
        // Decision-specific props
        onAnswerChange={onAnswerChange}
        onOptionsChange={onOptionsChange}
        hoveredBranch={hoveredBranch}
      />

      {/* Icon Picker */}
      {iconPicker.showPicker && (
        <IconPicker
          currentIcon={task.icon}
          onIconChange={iconPicker.changeIcon}
          onIconDeleted={onIconDeleted}
          onClose={iconPicker.closePicker}
          position={iconPicker.pickerPosition}
          nodeColor={currentCategory?.color}
        />
      )}

      {connectionMenu.showMenu && connectionMenu.menuPosition && (
        <ConnectionMenu
          position={connectionMenu.menuPosition}
          onSelect={connectionMenu.selectNodeType}
          onClose={connectionMenu.closeMenu}
          isMultiCampaignMode={isMultiCampaignMode}
          sourceCampaign={task.sourceCampaign}
          checkClipboard={checkClipboard}
          onCreateCampaign={onCreateCampaign
            ? () => onCreateCampaign({ sourceTaskId: task.id, mode: 'after' })
            : undefined}
        />
      )}

      {/* Bottom handles - branch outputs (Flow-view specific) */}
      <div className="decision-node__bottom-handles">
        {options.map((option) => (
          <div
            key={option.id}
            className={`decision-node__branch-btn ${answer === option.id ? 'decision-node__branch-btn--active' : ''} ${connectingBranch === option.id ? 'decision-node__branch-btn--connecting' : ''} ${hoveredBranch === option.id ? 'decision-node__branch-btn--hovered' : ''}`}
            style={{ '--option-color': option.color }}
            onMouseDown={handleBranchMouseDown(option.id)}
            onMouseEnter={() => setHoveredBranch(option.id)}
            onMouseLeave={() => setHoveredBranch(null)}
          >
            <span className="decision-node__branch-btn-label">{option.label.charAt(0)}</span>
            <span className="decision-node__branch-tooltip">{option.label}</span>
          </div>
        ))}
      </div>

      {/* Resize handles */}
      <ResizeHandles onResizeMouseDown={handleResizeMouseDown} />
    </div>
  );
};

export default DecisionNode;
